import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ChatRoom from 'pages/mobile/others/ChatRoom';
import ChatList from 'pages/mobile/others/ChatList';
import { Swiper, Button, Modal } from 'antd-mobile';
import { CloseCircleFill } from 'antd-mobile-icons';
import liff from '@line/liff';
import secureStorage from 'shared/secureStorage';

const Chat = () => {
  const userID = secureStorage.getItem('lineUserId');
  const wsConUrl = process.env.REACT_APP_CHATROOM_WS_URL;

  const [me, setMe] = useState({ user_id: '', user_info: {} }); // 我的資訊
  const [friends, setFriends] = useState({}); // 好友清單
  const [matchFriend, setMatchFriend] = useState({}); // 正在聊天的好友資訊
  const [incomingMsgFrom, setIncomingMsgFrom] = useState(''); // 剛剛傳了新訊息的好友ID
  const [oldScrollHeight, setOldScrollHeight] = useState(0); // 聊天室訊息串<div>渲染高度
  const [isVisibleDisconnect, setIsVisibleDisconnect] = useState(false); // WS斷線錯誤訊息提示
  const [isLoading, setIsLoading] = useState(true); // 是否正在載入聊天室功能畫面
  const [isLoadingMessages, setIsLoadingMessages] = useState(false); // 是否正在載入歷史訊息
  const [isForceFriend, setIsForceFriend] = useState(false); // 是否強制進入指定好友的聊天室，不展示好友清單

  // Swiper Ref
  const ref = useRef(null);
  const goToChatList = () => {
    ref.current.swipeTo(0);
  };
  const goToChatRoom = () => {
    ref.current.swipeTo(1);
  };

  // location state
  const location = useLocation();
  const { plateNumber = '' } = location.state || {}; // 由其他頁面帶入‘車牌號碼’

  // Line Liff
  const closeLiff = () => {
    liff.closeWindow();
  };

  // WebSocket
  const [ws, setWs] = useState(null);
  // 建立WS連線
  const connectWebSocket = (userID) => {
    setWs(new WebSocket(`${wsConUrl}/${userID}`));
  };
  // WS初始化
  const initWebSocket = () => {
    ws.onopen = () => {
      // 連線建立成功
      console.log('WebSocket Connected to:', ws.url);
      registerUserId();
      getFriendsMap();
    };

    ws.onmessage = (event) => {
      // 收到伺服器傳來的訊息
      const message = JSON.parse(event.data);
      console.log('Message Received:', message);

      if (message.message_type == 'online_user_list') {
        // 更新 我的資訊、在/離線狀態
        let users = message.data;
        const myData = users.find((item) => item.user_id === userID);
        setMe(myData);
        users = users.filter((item) => item.user_id !== userID);
        updateFriendsOnlineStatus(users);
      } else if (message.message_type == 'update_friends_map') {
        // 更新 好友清單
        updateFriends(message.data);
      } else if (message.message_type == 'personal_message') {
        // 收到個人訊息
        onPersonalMessage(message.data);
      } else if (message.message_type == 'is_read_by_user') {
        // 被其他用戶已讀
        isReadByFriend(message.data.user_id);
      } else if (message.message_type == 'update_previous_messages') {
        // 更新查詢出的歷史訊息
        updatePreviousMessages(message.data.update_messages);
      } else if (message.message_type == 'system_error') {
        // 系統錯誤訊息
        window.antdNotify('錯誤', message.data.message, 'fail');
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket Error:', error);
    };

    ws.onclose = (event) => {
      console.log('Disconnected');
      setIsVisibleDisconnect(true);
    };
  };

  // 將自己註冊到連線名單中
  const registerUserId = () => {
    ws.send(
      JSON.stringify({
        message_type: 'register_user_id',
        data: {
          user_id: userID,
        },
      })
    );
  };

  // 取得好友列表
  const getFriendsMap = () => {
    ws.send(
      JSON.stringify({
        message_type: 'get_friends_map',
        data: {},
      })
    );
  };

  // 更新好友清單
  const updateFriends = (friends) => {
    setFriends(friends);
    setIsLoading(false);
  };

  // 更新好友‘在線’、‘離線’狀態
  const updateFriendsOnlineStatus = (onlineUsers) => {
    // 好友清單
    setFriends((prevFriends) => {
      const updatedFriends = { ...prevFriends };
      for (let id of Object.keys(updatedFriends)) {
        const user = onlineUsers.find((item) => item.user_id === id);
        updatedFriends[id] = { ...updatedFriends[id], is_online: user ? 1 : 0 };
      }
      return updatedFriends;
    });
    // 正在聊天的好友
    setMatchFriend((prevMatchFriend) => {
      if (prevMatchFriend.user_id) {
        const updatedMatchFriend = { ...prevMatchFriend };
        const user = onlineUsers.find(
          (item) => item.user_id === prevMatchFriend.user_id
        );
        updatedMatchFriend.is_online = user ? 1 : 0;
        return updatedMatchFriend;
      } else {
        return prevMatchFriend;
      }
    });
  };

  // 開始與一個好友聊天
  const startChat = (withFriend) => {
    console.log('starting chat with:', withFriend);
    setMatchFriend(withFriend);
    if (withFriend.messages.length < 20) {
      getPreviousMessages(withFriend.user_id);
    }
    isReadFriend(withFriend.user_id);
    goToChatRoom();
  };

  // 結束聊天
  const endChat = (friendUserId) => {
    console.log('chat ended.');
    setFriends((prevFriends) => {
      const updatedFriends = { ...prevFriends };
      updatedFriends[friendUserId].messages = [];
      return updatedFriends;
    });
    setMatchFriend({});
    goToChatList();
  };

  // 送出文字訊息
  const sendMessage = (textData) => {
    if (matchFriend.user_id) {
      const trimmedTextData = textData.trim();
      if (trimmedTextData) {
        ws.send(
          JSON.stringify({
            message_type: 'send_message',
            data: {
              sender: userID,
              receiver: matchFriend.user_id,
              message: textData,
            },
          })
        );
      }
    }
  };

  // 收到個人訊息
  const onPersonalMessage = (message) => {
    const friendUserId =
      message.sender === userID ? message.receiver : message.sender;

    // 好友清單
    setFriends((prevFriends) => {
      const updatedFriends = { ...prevFriends };
      updatedFriends[friendUserId].messages.push(message);
      if (message.sender === userID) {
        updatedFriends[friendUserId].latest_message = '你: ' + message.message;
      } else {
        updatedFriends[friendUserId].latest_message = message.message;
      }
      updatedFriends[friendUserId].latest_message_time = message.send_time;
      updatedFriends[friendUserId].unread_count +=
        message.sender === userID ? 0 : 1;
      return updatedFriends;
    });

    // 這個人剛剛傳了訊息來，觸發新訊息提示機制
    setIncomingMsgFrom(message.sender);
  };

  // 將這個好友的訊息標記為已讀
  const isReadFriend = (friendUserId) => {
    if (friends[friendUserId].unread_count > 0) {
      setFriends((prevFriends) => {
        const updatedFriends = { ...prevFriends };
        updatedFriends[friendUserId].unread_count = 0;
        const updatedMessages = updatedFriends[friendUserId].messages.map(
          (msg) => ({
            ...msg,
            is_read: 1,
          })
        );
        updatedFriends[friendUserId].messages = updatedMessages;
        return updatedFriends;
      });
      // 告訴這個好友他被我已讀了
      ws.send(
        JSON.stringify({
          message_type: 'is_read_user',
          data: {
            is_read_user_id: friendUserId, // 被已讀的對象
          },
        })
      );
    }
  };

  // 被這個好友已讀了，所以我傳送給這個好友的訊息都要標記為已讀
  const isReadByFriend = (readByFriendId) => {
    setFriends((prevFriends) => {
      const updatedFriends = { ...prevFriends };
      updatedFriends[readByFriendId].messages.forEach((msg) => {
        if (msg.sender === userID && msg.receiver === readByFriendId) {
          msg.is_read = 1;
        }
      });
      setMatchFriend(updatedFriends[readByFriendId]);
      return updatedFriends;
    });
  };

  // 取得與這個好友的歷史聊天訊息
  const getPreviousMessages = (friendUserId) => {
    if (!isLoadingMessages || !friendUserId) {
      setIsLoadingMessages(true);
      ws.send(
        JSON.stringify({
          message_type: 'get_previous_messages',
          data: {
            friend_user_id: friendUserId,
            current_oldest_message_time:
              friends[friendUserId].messages.length > 0
                ? friends[friendUserId].messages[0].send_time
                : null,
          },
        })
      );
    }
  };

  // 將歷史訊息更新到相對應的好友訊息清單中
  const updatePreviousMessages = (previousMessages) => {
    if (previousMessages.length > 0) {
      const friendUserId =
        previousMessages[0].sender === userID
          ? previousMessages[0].receiver
          : previousMessages[0].sender;

      setFriends((prevFriends) => {
        const updatedFriends = { ...prevFriends };
        updatedFriends[friendUserId].messages.unshift(...previousMessages);
        return updatedFriends;
      });
    }
    setIsLoadingMessages(false);
  };

  useEffect(() => {
    if (!ws) {
      connectWebSocket(userID);
    } else {
      initWebSocket();
    }
  }, [ws]);

  return (
    <>
      <Swiper ref={ref} allowTouchMove={false} indicator={() => null}>
        <Swiper.Item key="chat-list">
          <div className="main-container">
            <ChatList
              isLoading={isLoading}
              me={me}
              friends={friends}
              startChat={startChat}
              plateNumber={plateNumber}
              setIsForceFriend={setIsForceFriend}
            />
          </div>
        </Swiper.Item>
        <Swiper.Item key="chat-room">
          <ChatRoom
            friend={matchFriend}
            endChat={endChat}
            sendMessage={sendMessage}
            isReadFriend={isReadFriend}
            isLoadingMessages={isLoadingMessages}
            getPreviousMessages={getPreviousMessages}
            oldScrollHeight={oldScrollHeight}
            setOldScrollHeight={setOldScrollHeight}
            isForceFriend={isForceFriend}
            incomingMsgFrom={incomingMsgFrom}
            setIncomingMsgFrom={setIncomingMsgFrom}
          />
        </Swiper.Item>
      </Swiper>
      <Modal
        visible={isVisibleDisconnect}
        content={
          <div className="fail-modal-content">
            <CloseCircleFill style={{ fontSize: 64, color: '#FF433C' }} />
            <div className="subheading font-bold">連線已中斷！</div>
            <div className="text-body font-normal fail-modal-text">
              請關閉頁面，再重新進入。
            </div>
            <Button block className="secondary-button" onClick={closeLiff}>
              關閉頁面
            </Button>
          </div>
        }
      />
    </>
  );
};

export default Chat;
